import uuid from 'react-native-uuid';
import {MinimalProduct, PremiumKey} from '../types';
import {getPremiumKey} from './billing';
import {findBy, writeItem} from './db';
import {erc20abi, sendCoin, signer} from './metamask';
import {
  ERC20__factory,
  ExchangeContract__factory,
  ExchangeService__factory,
  ExchangeToken__factory,
} from './typechain-types';
import * as ethers from 'ethers';
import {
  decryptAesKey,
  decryptPayload,
  encryptWithPassword,
  generateNewKeyPair,
} from './ssl';
import {IShopConfig} from '../types/shop';

export const exchangeTokenAddress = () =>
  networkToContract[window.ethereum?.networkVersion || 0];

export const networkToContract = {
  80002: '0x8eA0334032c200AeA65Af269d66b0544D06129e4',
  97: '0x8eA0334032c200AeA65Af269d66b0544D06129e4',
  137: '0x8eA0334032c200AeA65Af269d66b0544D06129e4',
  56: '0x8eA0334032c200AeA65Af269d66b0544D06129e4',
};

export const networks = {
  1: {
    chainId: '0x1',
    chainName: 'Ethereum Mainnet',
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18,
    },
    rpcUrls: ['https://eth.llamarpc.com'],
    blockExplorerUrls: ['https://etherscan.io'],
  },
  137: {
    chainId: '0x89',
    chainName: 'Polygon Mainnet',
    nativeCurrency: {
      name: 'MATIC',
      symbol: 'MATIC',
      decimals: 18,
    },
    rpcUrls: ['https://polygon-mainnet.infura.io'],
    blockExplorerUrls: ['https://polygonscan.com/'],
  },
  324: {
    chainId: '0x144',
    chainName: 'zkSync Mainnet',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    rpcUrls: ['https://mainnet.era.zksync.io'],
    blockExplorerUrls: ['https://era.zksync.network/'],
  },
  56: {
    chainId: '0x38',
    chainName: 'BNB Smart Chain',
    nativeCurrency: {
      name: 'BNB',
      symbol: 'BNB',
      decimals: 18,
    },
    rpcUrls: ['https://bsc-dataseed.binance.org/'],
    blockExplorerUrls: ['https://bscscan.com'],
  },
  97: {
    chainId: '0x61',
    chainName: 'BNB Smart Chain Testnet',
    nativeCurrency: {
      name: 'TBNB',
      symbol: 'TBNB',
      decimals: 18,
    },
    rpcUrls: ['https://data-seed-prebsc-1-s1.binance.org:8545/'],
    blockExplorerUrls: ['https://testnet.bscscan.com'],
  },
  80002: {
    chainId: '0x13882',
    chainName: 'Polygon Amoy Testnet',
    nativeCurrency: {
      name: 'MATIC',
      symbol: 'MATIC',
      decimals: 18,
    },
    rpcUrls: ['https://rpc-amoy.polygon.technology'],
    blockExplorerUrls: ['https://www.oklink.com/amoy'],
  },
};

export const address0 = '0x0000000000000000000000000000000000000000';

export const uniSwapLink =
  'https://app.uniswap.org/swap?theme=dark&inputCurrency=0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359&outputCurrency=0xD75dc076cDB0E692214371924B6640c545F688C6&chain=polygon';

export const connectToExchangeToken = async () =>
  new ethers.Contract(
    exchangeTokenAddress(),
    ExchangeToken__factory.abi,
    await signer(),
  );

export const getStorageAddress = async () => {
  const token = await connectToExchangeToken();
  const storageCategory = await token.categories('storage', 0);
  const storageAddress = storageCategory;
  return storageAddress;
};

export const getServicePaymentInfo = async (
  serviceAddress: string,
) => {
  const shop = await connectToExchangeService(serviceAddress);
  const coin = await shop.wp();
  const sig = await signer();
  const erc20 = new ethers.Contract(coin, ERC20__factory.abi, sig);
  const decimals = ethers.toBigInt(10) ** (await erc20.decimals());
  const price = await shop.defaultPrice();
  const subInterval = ethers.toNumber(await shop.requiresSubs());
  const value = ethers.toNumber(price / decimals);
  return {
    coinName: await erc20.symbol(),
    coinAddress: coin,
    subInterval,
    value,
  };
};

export const getShopServiceAddress = async () => {
  const token = await connectToExchangeToken();
  const serviceAddress = await token.categories('market', 0);
  return serviceAddress;
};

export const getUserStorageAddress = async () => {
  const token = await connectToExchangeToken();
  const serviceAddress = await token.categories('storage', 0);
  const shop = await connectToExchangeService(serviceAddress);
  const userContractAddress = await shop.userContracts(
    (
      await signer()
    ).address,
  );
  return userContractAddress;
};

export const getCurrentMetamaskWalletInfo = async () => {
  const token = await connectToExchangeToken();
  const wallet = await signer();
  const excdBalanceWithDecimals = await token.balanceOf(
    wallet.address,
  );
  const decimals = ethers.toBigInt(10) ** (await token.decimals());
  const excdBalance = ethers.toNumber(
    excdBalanceWithDecimals / decimals,
  );
  return {
    wallet,
    excdBalance,
  };
};

export const connectToExchangeContract = async (address: string) =>
  new ethers.Contract(
    address,
    ExchangeContract__factory.abi,
    await signer(),
  );

export const connectToExchangeService = async (address: string) =>
  new ethers.Contract(
    address,
    ExchangeService__factory.abi,
    await signer(),
  );

export const buyItemFromShop = async (
  shopAddress: string,
  value: number,
) => {
  const shop = await connectToExchangeService(shopAddress);
  const coin = await shop.wp();
  const contractAddress = (await (
    await (
      await shop.createBuyItemsContract(value, 1, shopAddress)
    ).wait(1)
  )?.contractAddress) as string;
  const contract = await connectToExchangeContract(contractAddress);
  const trx = await sendCoin(coin, contractAddress, value);
  const purchase = await contract.confirmPurchase();
  return trx;
};

export const checkServiceValid = async (serviceAddress: string) => {
  try {
    const shop = await connectToExchangeService(serviceAddress);
    const userContractAddress = await shop.userContracts(
      (
        await signer()
      ).address,
    );
    const isValid = await shop.validateSubscription(
      userContractAddress,
    );
    return isValid;
  } catch (err) {
    return false;
  }
};

const createServiceContract = async (
  shop: any,
  serviceAddress: string,
  value: number,
) => {
  const sig = await signer();
  const exdc = await connectToExchangeToken();
  await (
    await exdc.createServiceUserContract(
      value,
      await shop.wp(),
      1,
      await shop.provider(),
      sig.address,
      0,
      false,
      await shop.requiresSubs(),
      serviceAddress,
      serviceAddress,
    )
  ).wait(1);

  const contractAddress = await shop.userContracts(sig.address);
  const contract = await connectToExchangeService(contractAddress);
  return {contract, contractAddress};
};

export const buyServiceSmart = async (serviceAddress: string) => {
  try {
    const shop = await connectToExchangeService(serviceAddress);
    const coin = await shop.wp();
    const sig = await signer();
    const erc20 = new ethers.Contract(coin, erc20abi, sig);
    const price = await shop.defaultPrice();
    const subInterval = await shop.requiresSubs();
    const value = parseInt(
      ethers.formatUnits(price, await erc20.decimals()),
      10,
    );
    const abiCoder = new ethers.AbiCoder();
    const keyBlob = abiCoder.encode(['string'], [JSON.stringify({})]);
    const prevContractAddress = await shop.userContracts(sig.address);
    console.info(
      'prevContractAddress',
      prevContractAddress,
      sig.address,
    );

    if (prevContractAddress === address0) {
      console.info('previous contract is address zero');
      const {contract, contractAddress} = await createServiceContract(
        shop,
        serviceAddress,
        value,
      );
      await sendCoin(coin, contractAddress, price);
      await (await contract.activateService(keyBlob)).wait(1);
    } else {
      let contract = await connectToExchangeService(
        prevContractAddress,
      );
      const contractPrice = await contract.defaultPrice();
      const contractSubInterval = await contract.requiresSubs();
      console.info(
        contractPrice,
        price,
        value,
        contractSubInterval,
        subInterval,
      );
      if (
        contractPrice !== price ||
        contractSubInterval !== subInterval
      ) {
        let {contract, contractAddress} = await createServiceContract(
          shop,
          serviceAddress,
          value,
        );
        await sendCoin(coin, contractAddress, price);
        await (await contract.activateService(keyBlob)).wait(1);
        return;
      }
      const balance = await contract.balanceOfContract();
      if (balance >= price) {
        await (await contract.activateService(keyBlob)).wait(1);
      } else {
        await sendCoin(coin, prevContractAddress, price);
        await (await contract.activateService(keyBlob)).wait(1);
      }
    }
  } catch (err) {
    console.error('buyservicesmart error', err);
  }
};

export const getShopService = async () => {
  const sig = await signer();
  const market = await connectToExchangeService(
    await getShopServiceAddress(),
  );
  const subContract = await market.userContracts(sig.address);
  return {market, subContract, sig};
};

export const getMyShop = async (subContract: string) => {
  const sub = await connectToExchangeService(subContract);
  const contractAddress = await sub.services(0);
  return contractAddress;
};

export const createSmartService = async (config: IShopConfig) => {
  const excd = await connectToExchangeToken();
  const {sig, subContract} = await getShopService();
  try {
    const existing = await getMyShop(subContract);
    console.info('existing', existing);
    if (existing) {
      const service = await connectToExchangeService(existing);
      return {contract: service, contractAddress: existing};
    }
  } catch (err) {
    console.error(err);
  }

  const createContract = async () => {
    const contractTx = await (
      (await excd.createServiceUserContract(
        1,
        config.coin,
        1,
        sig.address,
        config.operator,
        parseInt(config.ratings),
        false,
        parseInt(config.subsPrice),
        config.category,
        subContract,
      )) as ethers.TransactionResponse
    ).wait(1);
    const contractAddress = await getMyShop(subContract);
    const service = await connectToExchangeService(contractAddress);
    return {contract: service, contractAddress};
  };
  const service = await createContract();
  return service;
};

export const getShopKey = async () => {
  let [premiumKey] = (await findBy(
    'folderId',
    'shop_keys3',
  )) as PremiumKey[];
  if (!premiumKey || !premiumKey.keyPair) {
    const auctionKeyId = uuid.v4().toString();
    const keyPair =
      (await generateNewKeyPair()) as PremiumKey['keyPair'];
    premiumKey = {
      id: auctionKeyId,
      keyPair,
      folderId: 'shop_keys3',
      isKeyPair: true,
    };
    await writeItem(premiumKey);
  }
  return premiumKey;
};

export const updateSmartServiceUserData = async (
  serviceAddress: string,
  shopProducts: MinimalProduct[],
  shopName: string,
  shopLogo: string,
  password?: string,
) => {
  console.info('shopproducts', shopProducts);
  const shop = await connectToExchangeService(serviceAddress);
  const abiCoder = new ethers.AbiCoder();
  const ordersPubKey = (await getShopKey()).keyPair.publicKey;
  let params: Object = {
    shopProducts,
    ordersPubKey,
    shopName,
    shopLogo,
  };
  if (password) {
    params = await encryptWithPassword(
      password,
      JSON.stringify(params),
    );
  }
  console.info('shopParams', params);
  const dataBlob = abiCoder.encode(
    ['string'],
    [JSON.stringify(params)],
  );
  const tx = await (await shop.changeUserData(dataBlob)).wait(1);
  return tx;
};

const stateToText = {
  0: 'Created',
  1: 'Locked',
  2: 'Delivered',
  3: 'Release',
  4: 'Inactive',
};

export const getOrders = async (serviceAddress: string) => {
  const abiCoder = new ethers.AbiCoder();
  const shop = await connectToExchangeService(serviceAddress);
  const orders = await shop.getContracts();
  console.info(orders);
  const orderContracts = await Promise.all(
    orders.map(async (i: string) => {
      let userData: Object = {};
      let encData: Object = {};
      try {
        const contract = await connectToExchangeContract(i);
        const st = ethers.formatUnits(await contract.state(), 0);
        const price = ethers.formatUnits(
          await contract.price(),
          await contract.decimals(),
        );
        try {
          encData = JSON.parse(
            abiCoder.decode(
              ['string'],
              await contract.buyerData(),
            )[0],
          );
          //   console.info('encData', encData);
          //   const key = await decryptAesKey(
          //     encData.encKey,
          //     shopK as JsonWebKey,
          //   );
          //   const {encryptedPayload, iv} = encData.payload;
          //   userData = JSON.parse(
          //     await decryptPayload(
          //       key,
          //       encryptedPayload as string,
          //       iv as string,
          //     ),
          //   );
        } catch (err) {
          console.error(err);
        }
        const data = {
          id: i,
          state: stateToText[st],
          price,
          userData,
          contract,
          encData,
          rating: await contract.rating(),
        };
        return data;
      } catch (err) {
        console.error('mapping err', err);
        return {id: i, userData, encData};
      }
    }),
  );
  console.info(orderContracts);
  return orderContracts.reverse();
};

export const getOrderById = async (orderId: string) => {
  const abiCoder = new ethers.AbiCoder();
  const shopKeys = await getShopKey();
  const shopK = shopKeys.keyPair.privateKey;
  const contract = await connectToExchangeContract(orderId);
  const st = parseInt(
    ethers.formatUnits(await contract.state(), 0),
    10,
  ) as keyof typeof stateToText;
  const price = ethers.formatUnits(
    await contract.price(),
    await contract.decimals(),
  );
  let userData: Object = {};
  const encData = JSON.parse(
    abiCoder.decode(['string'], await contract.buyerData())[0],
  );

  try {
    const key = await decryptAesKey(
      encData.encKey,
      shopK as JsonWebKey,
    );
    const {encryptedPayload, iv} = encData.payload;
    userData = JSON.parse(
      await decryptPayload(
        key,
        encryptedPayload as string,
        iv as string,
      ),
    );
  } catch (err) {
    console.error(err);
  }
  const data = {
    id: orderId,
    state: stateToText[st],
    price,
    userData,
    contract,
    encData,
    rating: await contract.rating(),
  };
  return data;
};
